import { useMemo } from 'react';
import { WorkspaceMember } from '../auth/workspace';

type UserAvatarProps = {
  user?: WorkspaceMember;
  userId?: string;
  userName?: string;
  size?: number;
  className?: string;
  showTooltip?: boolean;
};

const avatarColors = [
  '#3B82F6',
  '#22C55E',
  '#F97316',
  '#EF4444',
  '#8B5CF6',
  '#6366F1',
  '#F43F5E',
  '#F59E0B',
  '#CA8A04',
  '#14B8A6',
];

/**
 * Generates a color based on the user ID or name
 */
function generateAvatarColor(id: string | undefined, name: string | undefined): string {
  const seed = id || name || 'unknown';
  const index =
    Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;

  return avatarColors[index];
}

/**
 * Gets the initials from a user's name
 */
function getInitials(name: string | undefined): string {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function UserAvatar({
  user,
  userId,
  userName,
  size = 32,
  className = '',
  showTooltip = true,
}: UserAvatarProps) {
  const id = user?.userId || userId;
  const name = user?.name || userName;

  const initials = useMemo(() => getInitials(name), [name]);
  const backgroundColor = useMemo(() => generateAvatarColor(id, name), [id, name]);

  return (
    <div
      aria-hidden
      className={`flex items-center justify-center rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor,
        color: 'white',
        fontSize: size / 2,
        fontWeight: 'bold',
      }}
      title={showTooltip ? name : undefined}
    >
      {initials}
    </div>
  );
}
